import { useId, useState, type FormEvent, type ReactElement } from 'react';

import { ModalShell } from '@/components/ui/ModalShell';
import { TextAreaField } from '@/components/ui/TextAreaField';
import { TextField } from '@/components/ui/TextField';
import { useToast } from '@/components/ui/toast';
import { CatalogField } from '@/features/document-registration/components/CatalogField';
import { titleError } from '@/features/document-registration/presentation';
import { useActiveCompany } from '@/lib/active-company-context';
import { ApiError, apiErrorReason } from '@/lib/api';
import { useMe } from '@/lib/me';
import { useCatalog } from '@/lib/quality-catalogs';

import { reasonError, requestError } from '../presentation';
import type { CreateRequestInput } from '../types';
import { useCreateRequest } from '../use-document-requests';

// Solicitud de documento (G3-1, 04-GC-P08 §6.A; reunión 30:24): un documento nuevo con
// tipo, proceso, nombre, motivo y dueño propuesto, o la modificación de un vigente (solo
// el motivo: tipo, proceso y nombre salen del documento). Calidad la aprueba o rechaza.

export type RequestMode =
  | { kind: 'nuevo' }
  | { kind: 'modificacion'; target: { id: string; code: string; title: string | null } };

export function RequestModal({
  mode,
  onClose,
}: {
  mode: RequestMode;
  onClose: () => void;
}): ReactElement {
  const formId = useId();
  const { activeCompanyId } = useActiveCompany();
  const isNew = mode.kind === 'nuevo';
  const scope = { companyId: activeCompanyId, enabled: isNew };
  const types = useCatalog('document-types', scope);
  const areas = useCatalog('process-areas', scope);
  const create = useCreateRequest();
  const me = useMe().data?.data.user_id ?? null;
  const { showToast } = useToast();
  const [documentTypeId, setType] = useState('');
  const [processAreaId, setArea] = useState('');
  const [title, setTitle] = useState('');
  const [reason, setReason] = useState('');
  // El solicitante se propone como dueño (spec D1); Calidad lo confirma o cambia al aprobar.
  const [proposeMe, setProposeMe] = useState(true);
  const [checked, setChecked] = useState(false);
  const [titleTaken, setTitleTaken] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const complete =
    reason.trim() !== '' &&
    (!isNew || (documentTypeId !== '' && processAreaId !== '' && title.trim() !== ''));
  const titleMsg = titleTaken ?? (checked && isNew ? titleError(title) : null);
  const reasonMsg = checked ? reasonError(reason) : null;

  function input(): CreateRequestInput {
    if (mode.kind === 'modificacion') {
      return { kind: 'modificacion', targetEntryId: mode.target.id, reason: reason.trim() };
    }
    const ownerUserId = proposeMe && me !== null ? me : undefined;
    return {
      kind: 'nuevo',
      documentTypeId,
      processAreaId,
      proposedTitle: title.trim(),
      reason: reason.trim(),
      ...(ownerUserId === undefined ? {} : { ownerUserId }),
    };
  }

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setChecked(true);
    const invalid = reasonError(reason) !== null || (isNew && titleError(title) !== null);
    if (!complete || busy || invalid) {
      return;
    }
    setBusy(true);
    setFormError(null);
    try {
      await create.mutateAsync(input());
      showToast(
        mode.kind === 'nuevo'
          ? `Solicitud «${title.trim()}» enviada a Calidad`
          : `Solicitud de modificación de ${mode.target.code} enviada a Calidad`,
      );
      onClose();
    } catch (err) {
      if (apiErrorReason(err) === 'title_retired') {
        setTitleTaken(requestError(err));
      } else if (err instanceof ApiError && err.status === 404 && mode.kind === 'modificacion') {
        setFormError('Ese documento ya no está disponible. Recarga la ficha.');
      } else {
        setFormError(requestError(err));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalShell
      open
      title={isNew ? 'Solicitar documento' : 'Solicitar modificación'}
      wide={isNew}
      busy={busy}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn mut sm" onClick={onClose} disabled={busy}>
            Cancelar
          </button>
          <button type="submit" form={formId} className="btn sm" disabled={!complete || busy}>
            {busy ? 'Enviando…' : 'Enviar solicitud'}
          </button>
        </>
      }
    >
      <p className="modal-body">
        {mode.kind === 'nuevo' ? (
          'Calidad revisa la solicitud, asigna el código y la aprueba o la rechaza.'
        ) : (
          <>
            Se pedirá la modificación de <strong className="code">{mode.target.code}</strong>
            {mode.target.title !== null && ` · ${mode.target.title}`}. Calidad la aprueba o la
            rechaza.
          </>
        )}
      </p>
      <form id={formId} className="form-grid" noValidate onSubmit={(e) => void submit(e)}>
        {isNew && (
          <>
            <CatalogField
              label="Tipo de documento"
              placeholder="Selecciona un tipo…"
              query={types}
              value={documentTypeId}
              onChange={setType}
            />
            <CatalogField
              label="Proceso"
              placeholder="Selecciona un proceso…"
              query={areas}
              value={processAreaId}
              onChange={setArea}
            />
            <TextField
              label="Nombre propuesto"
              value={title}
              maxLength={200}
              placeholder="Control de pesaje en recepción"
              error={titleMsg}
              onChange={(v) => {
                setTitleTaken(null);
                setTitle(v);
              }}
            />
          </>
        )}
        <TextAreaField
          label="Motivo"
          value={reason}
          maxLength={1000}
          placeholder="Qué se necesita y por qué"
          error={reasonMsg}
          onChange={setReason}
        />
        {isNew && me !== null && (
          <label className="chk">
            <input
              type="checkbox"
              checked={proposeMe}
              onChange={(e) => setProposeMe(e.target.checked)}
            />
            Propongo ser el dueño de proceso
          </label>
        )}
        {formError && (
          <p className="fld-error" role="alert">
            {formError}
          </p>
        )}
      </form>
    </ModalShell>
  );
}
